import Link from "next/link";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { type ReactNode } from "react";

import Text from "@/blueprint/components/Text/Text";
import { cn } from "@/lib/utils";

/**
 * Figma's `Button w icon` (`76:1204`) — a label followed by a filled circle
 * holding an arrow. It reads as a link rather than a button, and it hugs its
 * label at every breakpoint.
 *
 * The circle is a constant 24 and the label rides the paragraph-lg ramp
 * (16/24 → 18/26 → 20/28 → 22/32), so the gap of 12 is the only spacing the
 * component owns. Instances vary only in the circle's tone — white on the dark
 * band, ink on cream — and in which way the arrow points.
 *
 * The Figma text node is still named "Work With Us" from the careers component
 * it was instanced from; the label is always passed in, never defaulted.
 */
type ArrowLinkProps = {
  href: string;
  tone?: "light" | "dark";
  direction?: "up-right" | "right";
  /** Opens in a new tab — for off-site destinations like the Ashby board. */
  external?: boolean;
  onClick?: () => void;
  className?: string;
  children: ReactNode;
};

/**
 * Figma `CTAs` (`5289:2070`) — Link with arrow, Dark (`1:113`) and Light
 * (`9945:49255`). The circle is what carries the state: filled at rest, it
 * empties to an outline of the same colour on hover, and On Click is drawn
 * identically to Hover. The label holds its colour throughout.
 *
 * The edge is present in both states and merely transparent at rest, so the
 * 24px box and the space inside it never change — the arrow cannot shift.
 */
const CIRCLE_TONE_CLASSES = {
  light: [
    "border-transparent bg-brand-white text-brand-black",
    "group-hover:border-brand-white group-hover:bg-transparent group-hover:text-brand-white",
    "group-active:border-brand-white group-active:bg-transparent group-active:text-brand-white",
  ].join(" "),
  dark: [
    "border-transparent bg-content-primary text-content-inverse",
    "group-hover:border-content-primary group-hover:bg-transparent group-hover:text-content-primary",
    "group-active:border-content-primary group-active:bg-transparent group-active:text-content-primary",
  ].join(" "),
} as const;

/**
 * `tone` used to recolour the circle and leave the label to whatever it
 * inherited, so a `light` instance only read as light where an ancestor had
 * already flipped `content-primary` — true of the one place it is used, and
 * not of a dark surface that scopes no theme.
 *
 * `light` is stated as `brand-white` rather than `content-inverse`: the dark
 * theme inverts that token to ink, which would turn the label black on
 * exactly the surfaces this tone exists for. `brand-white` is absolute.
 */
const LABEL_TONE_CLASSES = {
  light: "text-brand-white",
  dark: "text-content-primary",
} as const;

const ArrowLink = ({
  href,
  tone = "light",
  direction = "up-right",
  external = false,
  onClick,
  className,
  children,
}: ArrowLinkProps) => {
  const Arrow = direction === "right" ? ArrowRight : ArrowUpRight;

  return (
    <Link
      href={href}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
      {...(onClick !== undefined ? { onClick } : {})}
      className={cn(
        "group relative inline-flex w-fit items-center gap-[12px]",
        /* The lockup is only as tall as its label ramp (24 to 32), so the
           target is grown to 44 by a pseudo-element rather than by
           padding the drawn row. */
        "before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-11 before:min-w-full before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']",
        /* The circle carries every state on its own, so the link itself
           transitions nothing and never transforms. Figma draws On Click at
           the same 24 as the other states — see `CIRCLE_TONE_CLASSES` — so
           the blueprint's press scale would have been the one thing here the
           node does not ask for. */
        LABEL_TONE_CLASSES[tone],
        className,
      )}
    >
      <Text variant="paragraph-lg" as="span" tone="inherit">
        {children}
      </Text>
      <span
        className={cn(
          "flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border",
          "transition-[color,background-color,border-color] duration-quick ease-out-strong motion-reduce:transition-none",
          CIRCLE_TONE_CLASSES[tone],
        )}
      >
        <Arrow aria-hidden strokeWidth={1.25} className="h-[18px] w-[18px]" />
      </span>
    </Link>
  );
};

export default ArrowLink;
